import type { FunctionComponent } from 'react';
import { useCallback, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { LanguagePicker } from '~/ui/domain/language-picker/LanguagePicker';
import { ActionButtons, HeaderContainer, Seperator } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { StyledStickyHeader } from '../components/Common';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';

export const CompareVersionHeader: FunctionComponent<{
    readonly onClose: () => void;
}> = ({ onClose }) => {
    const { translate } = useContext(ApplicationContext);
    const { activeLanguage, availableLanguages, setActiveLanguage } = useActiveLanguage();
    const { updateCompareTileLanguage } = useJobArchitectureUrlTileActions();

    const handleLanguageChange = useCallback(
        (language: LanguageCodeForJSA) => {
            setActiveLanguage(language);
            updateCompareTileLanguage(language);
        },
        [setActiveLanguage, updateCompareTileLanguage],
    );

    return (
        <StyledStickyHeader style={{ margin: 0, paddingLeft: 0, paddingRight: 0, width: '100%' }}>
            <HeaderContainer>
                <ActionButtons>
                    <Button
                        aria-label={translate('common.close')}
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        onClick={onClose}
                    >
                        <IconCrossSmall />
                    </Button>
                    <Seperator />
                    <LanguagePicker
                        activeText={translate(`common.languages.${activeLanguage}` as TranslationKey)}
                        disabled={false}
                        isGenerating={false}
                        languages={availableLanguages}
                        onLanguageChange={handleLanguageChange}
                    />
                </ActionButtons>
            </HeaderContainer>
        </StyledStickyHeader>
    );
};
